/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module AllAlertsModule
 */
/* eslint-disable max-classes-per-file */

import {
    AfterViewChecked,
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    CUSTOM_ELEMENTS_SCHEMA,
    Inject,
    NgModule,
    NO_ERRORS_SCHEMA,
    OnDestroy,
    OnInit,
    TemplateRef,
    ViewChild,
} from '@angular/core';

import { L10nService } from '@vmw/ngx-vip';

import {
    IAviDiffGridConfig,
    IAviDiffGridRow,
} from 'ng/modules/data-grid/components/avi-diff-grid/avi-diff-grid.types';

import {
    IAviCollectionDataGridConfig,
    IAviCollectionDataGridRow,
    IAviDataGridConfig,
    IAviDataGridRow,
} from 'ng/modules/data-grid';

import {
    AlertCollection,
    TAlertCollection,
} from 'collections/alert.collection.factory';

import {
    AlertItem,
    eventIdToFields,
} from 'items/alert.item.factory';

import {
    AlertConfigItem,
    TAlertConfigItem,
} from 'ajs/modules/alert/factories/alert-config/alert-config.item.factory';

import { Auth } from 'ajs/modules/core/services/auth';
import * as globalL10n from 'global-l10n';
import { AviDatePipe } from 'ng/modules/core/pipes/avi-date.pipe';
import './all-alerts-list-page.component.less';
import { DataGridModule } from 'ng/modules/data-grid/data-grid.module';
import { SharedModule } from 'ng/shared/shared.module';
import { ClrSpinnerModule } from '@clr/angular';
import { AviFormsModule } from 'ng/modules/avi-forms/avi-forms.module';
import { CoreModule } from 'ng/modules/core/core.module';
import { CommonModule } from '@angular/common';
import { Store } from '@ngrx/store';
import { selectTimeframe } from 'ng/root-store/user-preferences';

import {
    AviPermissionResource,
    Privilege,
} from 'generated-types';

import { StringService } from 'ng/modules/core/services/string/string.service';
import { Subscription } from 'rxjs';
import { skip } from 'rxjs/operators';
import * as l10n from './all-alerts-list-page.l10n';

const { ENGLISH: dictionary, ...l10nKeys } = l10n;
const { ...globalL10nKeys } = globalL10n;

/**
 * @description All Alerts List Page Component.
 * @author Shahab Hashmi
 */

@Component({
    selector: 'all-alerts-list-page',
    templateUrl: './all-alerts-list-page.component.html',
})
export class AllAlertsListPageComponent implements OnInit, AfterViewInit, OnDestroy,
    AfterViewChecked {
    /**
     * Expander for sub grids template ref.
     */
    @ViewChild('expandedContentTemplateRef')
    public readonly expandedContentTemplateRef: TemplateRef<HTMLElement>;

    /**
     * Template ref for level color codes.
     */
    @ViewChild('levelColorTemplateRef')
    public readonly levelColorTemplateRef: TemplateRef<HTMLElement>;

    /**
     * Expander for Related Alerts sub grid.
     */
    @ViewChild('expandedContentRelatedEventsTemplateRef')
    public readonly expandedContentRelatedEventsTemplateRef: TemplateRef<HTMLElement>;

    /**
     * Config for collection grid.
     */
    public alertCollectionGridConfig: IAviCollectionDataGridConfig;

    /**
     * Config for Config Alerts grid.
     */
    public alertCaGridConfig: IAviDataGridConfig;

    /**
     * Config for Related Events Grid.
     */
    public alertEventsGridConfig: IAviDataGridConfig;

    public actionScriptOutputGridConfig: IAviDiffGridConfig;

    public alertCollection: AlertCollection;

    public readonly globalL10nKeys = globalL10nKeys;

    public readonly l10nKeys = l10nKeys;

    private timeframeSubscription: Subscription;

    constructor(
        private readonly l10nService: L10nService,
        @Inject(AlertCollection)
        private readonly AlertCollection: TAlertCollection,
        @Inject(AlertConfigItem)
        private readonly AlertConfig: TAlertConfigItem,
        private readonly aviDate: AviDatePipe,
        private readonly authService: Auth,
        private readonly stringService: StringService,
        private store: Store,
        @Inject('Timeframe')
        private readonly timeframeService: any,
        private cdr: ChangeDetectorRef,
    ) {
        this.l10nService.registerSourceBundles(dictionary);
        this.alertCollection = new AlertCollection();
        this.setTsDuration();
    }

    /** override */
    public ngOnInit(): void {
        const { objectName } = this.alertCollection;

        this.alertCollectionGridConfig = {
            id: `${objectName}-grid`,
            collection: this.alertCollection,
            fields: [],
            layout: {},
        };

        this.timeframeSubscription = this.store.select(selectTimeframe)
            .pipe(skip(1))
            .subscribe(() => {
                // Reset collection to clear data and pagination state.
                this.alertCollection.reset();
                this.setTsDuration();
                this.alertCollection.load();
            });

        this.actionScriptOutputGridConfig = {
            leftField: {
                label: this.l10nService.getMessage(l10nKeys.actionScriptOutputLabel),
            },
            rightField: {
                label: this.l10nService.getMessage(l10nKeys.actionScriptOutputLabel),
            },
            layout: {
                placeholderMessage: this.l10nService.getMessage(globalL10nKeys.noItemsFoundLabel),
            },
            isGridExpandable: true,
            noDiff: true,
        };
    }

    /**
     * Return action_script_output from row.
     */
    public getDiffGridRowsFromOutput(output: string): IAviDiffGridRow[] {
        return [{ left: output }];
    }

    /**
     * Return type of value.
     */
    public getType(val: boolean | string): boolean | string {
        return typeof val === 'string' || 'boolean';
    }

    /**
     * Return gridconfig props based on type.
     */
    public getAllAlertsChildGridConfig(type: string): IAviDataGridConfig {
        const {
            aviDate,
            authService,
            l10nService,
            stringService,
        } = this;

        const {
            alertConfigLabel,
            editAlertConfigLabel,
            timestampLabel,
            resourceNameLabel,
            resourceTypeLabel,
            eventCodeLabel,
            userLabel,
        } = l10nKeys;

        const {
            descriptionLabel,
            noItemsFoundLabel,
        } = globalL10nKeys;

        const tempConfig: IAviDataGridConfig = {
            fields: [],
            layout: {
                placeholderMessage: l10nService.getMessage(noItemsFoundLabel),
                hideCheckboxes: true,
                hidePagination: true,
            },
            expanderDisabled: (row: IAviDataGridRow) => !row.data.config.event_details,
            singleactions: [],
        };

        switch (type) {
            case 'ca':
                tempConfig.fields.push({
                    id: 'alert-config-name',
                    sortBy: 'alert-config-name',
                    label: l10nService.getMessage(alertConfigLabel),
                    transform: (row: IAviDataGridRow) => stringService.name(
                        row.getConfig().alert_config_ref,
                    ),
                });

                if (authService.isPrivilegeAllowed(
                    AviPermissionResource.PERMISSION_ALERTCONFIG,
                    Privilege.WRITE_ACCESS,
                )) {
                    tempConfig.singleactions = [
                        {
                            label: l10nService.getMessage(editAlertConfigLabel),
                            shape: 'pencil',
                            onClick: (row: AlertItem) => {
                                new this.AlertConfig({
                                    id: stringService.slug(row.getConfig().alert_config_ref),
                                    data: {
                                        config: {},
                                    },
                                }).edit();
                            },
                        },
                    ];
                }

                tempConfig.expandedContentTemplateRef = undefined;
                break;

            case 'events':
                tempConfig.fields.push({
                    id: 'timestamp',
                    sortBy: 'timestamp',
                    label: l10nService.getMessage(timestampLabel),
                    transform: (row: IAviDataGridRow) => aviDate.transform(
                        row.data.config.report_timestamp,
                    ),
                }, {
                    id: 'resource-name',
                    sortBy: 'resource-name',
                    label: l10nService.getMessage(resourceNameLabel),
                    transform: (row: IAviDataGridRow) => row.data.config.obj_name,
                }, {
                    id: 'resource-type',
                    sortBy: 'resource-type',
                    label: l10nService.getMessage(resourceTypeLabel),
                    transform: (row: IAviDataGridRow) => row.data.config.obj_type,
                }, {
                    id: 'event-code',
                    sortBy: 'event-code',
                    label: l10nService.getMessage(eventCodeLabel),
                    transform: (row: IAviDataGridRow) => row.data.config.event_id,
                }, {
                    id: 'user',
                    sortBy: 'user',
                    label: l10nService.getMessage(userLabel),
                    transform: (row: IAviDataGridRow) => this.getUser(row),
                }, {
                    id: 'description',
                    sortBy: 'description',
                    label: l10nService.getMessage(descriptionLabel),
                    transform: (row: IAviDataGridRow) => (
                        !row.data.config.event_description ?
                            l10nService.getMessage(globalL10nKeys.notApplicableLabel) :
                            row.data.config.event_description
                    ),
                });

                tempConfig.expandedContentTemplateRef = this.expandedContentRelatedEventsTemplateRef;
                break;
        }

        return tempConfig;
    }

    /** @override */
    public ngAfterViewInit(): void {
        const { l10nService } = this;

        const {
            levelLabel,
            resourceNameLabel,
            timestampLabel,
        } = l10nKeys;

        const {
            noItemsFoundLabel,
            summaryLabel,
        } = globalL10nKeys;

        this.alertCollectionGridConfig = {
            ...this.alertCollectionGridConfig,
            fields: [{
                id: 'timestamp',
                sortBy: 'timestamp',
                label: l10nService.getMessage(timestampLabel),
                transform: (row: IAviCollectionDataGridRow) => this.aviDate
                    .transform(row.getConfig().timestamp),
            }, {
                id: 'obj-name',
                sortBy: 'obj_name',
                label: l10nService.getMessage(resourceNameLabel),
                transform: (row: IAviCollectionDataGridRow) => row.getConfig().obj_name,
            }, {
                id: 'level',
                sortBy: 'level',
                label: l10nService.getMessage(levelLabel),
                templateRef: this.levelColorTemplateRef,
            }, {
                id: 'summary',
                label: l10nService.getMessage(summaryLabel),
                transform: (row: IAviCollectionDataGridRow) => row.getConfig().summary,
            }],
            multipleactions: [
                {
                    label: l10nService.getMessage(globalL10nKeys.deleteLabel),
                    id: 'delete',
                    onClick: (rows: IAviCollectionDataGridRow[]) => rows.map(row => row.drop()),
                },
            ],
            layout: {
                placeholderMessage: l10nService.getMessage(noItemsFoundLabel),
                hideDelete: true,
                hideEdit: true,
                hideReferenceCheckAction: true,
            },
            expandedContentTemplateRef: this.expandedContentTemplateRef,
        };

        this.alertCaGridConfig = this.getAllAlertsChildGridConfig('ca');
        this.alertEventsGridConfig = this.getAllAlertsChildGridConfig('events');
    }

    /** @override */
    public ngOnDestroy(): void {
        this.alertCollection.destroy();

        if (this.timeframeSubscription) {
            this.timeframeSubscription.unsubscribe();
        }
    }

    /** @override */
    public ngAfterViewChecked(): void {
        if (this.expandedContentTemplateRef) {
            this.cdr.detectChanges();
        }
    }

    /**
     * True if value is not an object or array.
     */
    public isPrimitive(value: any): boolean {
        return typeof value !== 'object';
    }

    /**
     * Set ts_duration to filter alerts.
     */
    private setTsDuration(): void {
        const { range } = this.timeframeService.selected();

        this.alertCollection.setParams({
            ts_duration: range,
            sort: '-timestamp',
        });
    }

    /**
     * Return user from event_details based on event_id.
     */
    private getUser({ event_details: eventDetails, event_id: eventId }: IAviDataGridRow): string {
        // Validate if eventId is a valid key in eventIdToFields
        if (!(eventId in eventIdToFields)) {
            return 'N/A';
        }

        // Retrieve user from eventDetails using propName.
        const propName = eventIdToFields[eventId as keyof typeof eventIdToFields];

        return eventDetails[propName]?.user ?? 'N/A';
    }
}

@NgModule({
    declarations: [AllAlertsListPageComponent],
    imports: [
        DataGridModule,
        CoreModule,
        CommonModule,
        SharedModule,
        AviFormsModule,
        ClrSpinnerModule,
    ],
    schemas: [
        CUSTOM_ELEMENTS_SCHEMA,
        NO_ERRORS_SCHEMA,
    ],
})
/* eslint-disable-next-line no-unused-vars */
class AllAlertsModule {}
